/**
 * La RUTA DEL SUPERVISOR. Módulo PURO: sin red, sin `Date`, sin Supabase.
 *
 * El supervisor no marca asistencia —decisión del usuario del 2026-09-05: *«ya no pongamos para
 * marcar ingreso y salida… dejémoslo sin seguimiento de asistencia»*—. Su unidad de trabajo es la
 * **visita**: llega a una tienda, deja constancia, y a veces le cuelga un formulario de
 * levantamiento.
 *
 * Las tres reglas que ordenan este módulo, todas dichas por el usuario:
 *
 *  1. **La ruta la elige él, sobre la marcha.** *«Dependiendo qué va viendo que le queda más cerca
 *     para avanzar rápido»*. No hay lista del día que cumplir ni orden que respetar.
 *  2. **El formulario está SIEMPRE abierto.** No depende de tener asignación, ni turno, ni
 *     campaña: *«que siempre lo tenga abierto, porque puede subir la información en cualquier
 *     momento del día»*.
 *  3. **Se puede registrar después.** *«Confiamos en que la levante, y si no la levantó en el
 *     momento, lo hace después»*. La contrapartida es la bandera `registro_tardio`, que pone el
 *     servidor: se acepta el registro diferido, no se disimula.
 *
 * La visita puede colgar de una campaña, de un estudio, o de **ninguna de las dos** — que es el
 * caso del levantamiento por iniciativa propia para una propuesta de negocio.
 */

/** Los formularios que se pueden levantar EN una parada. Los valores viven en la 0092/0096. */
export type Instrumento = 'pg_gondola' | 'bodega';

/** El canal comercial del punto de venta (`pdv.canal`, 0096). */
export type Canal = 'moderno' | 'tradicional' | 'mayorista';

/**
 * Los formularios adicionales, **que se suman a la visita y no la reemplazan**.
 *
 * Corrección del usuario del 2026-09-05: la primera versión hacía elegir UNA cosa entre «solo la
 * visita», «góndola» y «bodega». Está mal planteado y él lo explicó bien: *«el supervisor siempre
 * es parte de su visita el ir a visitar a la impulsadora; las otras acciones son adicionales a la
 * visita… acá yo puedo poner que solo fueron a hacer el levantamiento de la bodega, ok, pero
 * también van a la visita»*.
 *
 * Entonces: la visita a la impulsadora es una casilla propia, encendida por defecto, y estos son
 * extras que se marcan **además** y pueden ir los dos a la vez.
 *
 * `canales` decide qué puntos de venta se le ofrecen. Es lo que evita el otro error que reportó:
 * *«cuando es bodegas… no debería salir Makro ni Plaza Vea ni Tottus»*.
 */
export interface OpcionDeInstrumento {
  valor: Instrumento;
  etiqueta: string;
  ayuda: string;
  /** Cómo se llama el lugar en ESTE formulario. Una bodega no es una «tienda». */
  rotuloDelPunto: string;
  canales: readonly Canal[];
}

export const INSTRUMENTOS: readonly OpcionDeInstrumento[] = [
  {
    valor: 'pg_gondola',
    etiqueta: 'Góndola de canal moderno',
    ayuda: 'La ficha de anaquel: espacio, surtido, precios y competencia.',
    rotuloDelPunto: 'tienda',
    // Makro entra como `mayorista`: es cash & carry, y el estudio de agosto se hizo justamente
    // ahí. Ver BITACORA §33, donde quedó anotado que eso cambia lo que el estudio puede afirmar.
    canales: ['moderno', 'mayorista'],
  },
  {
    valor: 'bodega',
    etiqueta: 'Bodega o mercado (canal tradicional)',
    ayuda: 'La ficha del canal tradicional: exhibición, surtido y el contacto del bodeguero.',
    rotuloDelPunto: 'punto de venta',
    canales: ['tradicional'],
  },
];

/**
 * Cómo se le llama al lugar según lo que se esté levantando.
 *
 * Reportado por el usuario: *«cuando es bodegas, acá no es una tienda… puede ser mercado o puede
 * ser una bodega de calle. Está mal redactada la pregunta»*. Tenía razón: llamarle «tienda» a una
 * bodega de esquina hace dudar de si la pregunta es para uno.
 */
export function rotuloDelPunto(instrumentos: readonly Instrumento[]): string {
  const soloBodega = instrumentos.length > 0 && instrumentos.every((i) => i === 'bodega');
  return soloBodega ? 'punto de venta' : 'tienda';
}

/** Los canales cuyos puntos de venta tiene sentido ofrecer para lo que se va a levantar. */
export function canalesOfrecidos(instrumentos: readonly Instrumento[]): Canal[] {
  if (instrumentos.length === 0) return ['moderno', 'tradicional', 'mayorista'];
  const canales = new Set<Canal>();
  for (const i of instrumentos) {
    for (const c of INSTRUMENTOS.find((x) => x.valor === i)?.canales ?? []) canales.add(c);
  }
  return [...canales];
}

/** Una tienda que se puede elegir: de la maestra `pdv`, o de la lista del estudio de P&G. */
export interface TiendaElegible {
  /** `null` cuando la tienda no está en la maestra y solo existe como nombre. */
  pdvId: string | null;
  nombre: string;
  /** Cadena o zona, para distinguir dos tiendas con nombre parecido. */
  detalle: string | null;
}

/** Lo que la pantalla arma y manda a `registrar_visita_de_ruta`. */
export interface VisitaDeRuta {
  fecha: string;
  /** ISO del momento en que estuvo en la tienda; vacío si la carga de memoria. */
  ocurrioEn: string;
  pdvId: string | null;
  tiendaTexto: string;
  campaignId: string | null;
  estudioId: string | null;
  impulsadoraId: string | null;
  /** Lo normal de una ruta. En falso solo cuando la parada fue únicamente a levantar información. */
  visitoImpulsadora: boolean;
  instrumentos: Instrumento[];
  notas: string;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  fotoPath: string | null;
}

/**
 * Lo que falta para poder registrar, o `null` si está lista.
 *
 * Es deliberadamente corto. Lo único que se exige es lo que sin ello no sería una visita: **dónde**
 * y **cuándo**. La foto NO se exige acá aunque la pantalla la pida: un supervisor al que la tienda
 * no lo dejó fotografiar tiene que poder registrar igual, y la fila queda con `foto_path` vacío,
 * que se ve en la bandeja. Es la lección de las 46 fichas (BITACORA §29): un formulario que se
 * niega a guardar produce cero datos, no datos mejores.
 */
export function loQueFaltaEnVisita(v: VisitaDeRuta): string | null {
  if (v.fecha.trim() === '') return 'Elige la fecha de la visita.';
  const punto = rotuloDelPunto(v.instrumentos);
  if (v.pdvId === null && v.tiendaTexto.trim() === '') {
    return `Elige el ${punto} de la lista o escribe su nombre.`;
  }
  if (v.tiendaTexto.trim() !== '' && v.pdvId === null && v.tiendaTexto.trim().length < 3) {
    return `El nombre del ${punto} es muy corto: escríbelo completo.`;
  }
  // Una parada tiene que ser algo: o fue a ver a alguien, o fue a levantar algo. Sin ninguna de
  // las dos, la fila diría que estuvo en una tienda y nada más, que no sirve para nada.
  if (!v.visitoImpulsadora && v.instrumentos.length === 0) {
    return 'Marca si visitaste a la impulsadora o qué formulario levantaste.';
  }
  return null;
}

/**
 * ¿Esta visita se está registrando fuera del día en que ocurrió?
 *
 * Sirve para AVISAR en la pantalla antes de mandar, no para impedir. La bandera que queda guardada
 * la calcula el servidor: si esto viajara en el envío, declarar la fecha de hoy bastaría para
 * borrar el rastro de que se llenó tarde.
 */
export function esRegistroTardio(fechaVisita: string, hoy: string): boolean {
  return fechaVisita.trim() !== '' && fechaVisita < hoy;
}

/** El nombre con el que se muestra la tienda de una visita ya guardada. */
export function nombreDeTienda(v: { tiendaNombre: string | null; tiendaTexto: string | null }): string {
  return v.tiendaNombre ?? (v.tiendaTexto ?? '—');
}

/**
 * Cobertura de la semana: a cuántas de sus tiendas llegó.
 *
 * El usuario lo puso como la vara del trabajo del supervisor: *«por semana visita al menos una vez
 * a todas las impulsadoras»*. Se cuenta por TIENDA y no por visita: pasar tres veces por la misma
 * no cubre las otras dos.
 *
 * `aCargo` sale de `campaign_pdv_supervisors` (pestaña «Supervisores» de cada campaña, 0206); la
 * casilla vieja `pdv.supervisor_id` ya no la lee nadie desde la 0227. Si nadie
 * cargó el reparto, `aCargo` viene vacío y esto devuelve `null` en vez de un 0 % que acusaría al
 * supervisor de algo que no hizo.
 */
export function coberturaDeLaSemana(
  aCargo: string[], visitadas: string[],
): { cubiertas: number; total: number; pct: number } | null {
  if (aCargo.length === 0) return null;
  const vistas = new Set(visitadas);
  const cubiertas = aCargo.filter((id) => vistas.has(id)).length;
  return { cubiertas, total: aCargo.length, pct: Math.round((cubiertas / aCargo.length) * 100) };
}
